import React, { Component } from 'react';
import axios from 'axios';
import phoneFormatter from 'phone-formatter';
import Skeleton from '@material-ui/lab/Skeleton';

import MessageIcon from '@material-ui/icons/Message';
import CallIcon from '@material-ui/icons/Call';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import styled from 'styled-components';
import PaymentButtons from './PaymentButtons';
import getAmountLabel from '../../utils/getAmountLabel';
import getDateLabel from '../../utils/getDateLabel';
import { getStripeInvoiceAppearance } from '../../utils/stripeInvoiceAppearance';
import { ENABLED_CHECKOUT_METHODS } from '../../utils/checkoutPaymentMethodOrder';

import {
  Address,
  Amount,
  BusinessName,
  Branding,
  Date,
  Description,
  InvoiceBody,
  InvoiceMainRegion,
  InvoiceContainer,
  TopSection,
  MiddleSection,
  CheckoutActionSlot,
  ConvenienceFee,
  TotalAmount,
  ModernDivider,
  PaymentDetailsBox,
} from '../styled-components';

import Receipt from './Receipt';
import dictionary from '../../utils/dictionary';

const acceptableLanguages = ['en', 'fr', 'es'];

const CHEKKIT_GREEN = '#03B856';

/** Initials for logo tile when URL missing or image fails (S3 404). */
function getBusinessInitials(name, username) {
  const trimmedName = name && String(name).trim();
  if (trimmedName) {
    const initials = trimmedName
      .split(/\s+/)
      .map((word) => {
        const firstLetterOrDigit = word.match(/[\p{L}\p{N}]/u);
        return firstLetterOrDigit ? firstLetterOrDigit[0] : '';
      })
      .join('')
      .toUpperCase()
      .slice(0, 2);
    if (initials) return initials;
  }
  const u = username && String(username).trim();
  if (u) {
    const local = u.split('@')[0] || u;
    const two = local.slice(0, 2).toUpperCase();
    return two || '?';
  }
  return '?';
}

/**
 * Loading UI does not receive hasConvenienceFee until the invoice API responds.
 * Default: reserve space for the fee breakdown so the card does not grow when the
 * invoice includes a convenience fee.
 * Optional query (when the link generator knows): ?cf=0 or ?hasConvenienceFee=false
 */
function assumeConvenienceFeeSkeleton(search) {
  if (!search || typeof search !== 'string') return true;
  const p = new URLSearchParams(search);
  let v = p.get('hasConvenienceFee');
  if (v == null || v === '') {
    v = p.get('cf');
  }
  if (v == null || v === '') return true;
  const lower = String(v).toLowerCase();
  return !(
    lower === '0' ||
    lower === 'false' ||
    lower === 'no'
  );
}

/** Width / height at or above which a logo is treated as a wordmark rather than an icon. */
const WIDE_LOGO_ASPECT_RATIO = 1.6;

/** Fixed-height slot so the page doesn't shift when the logo loads and picks its layout. */
const InvoiceLogoSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 64px;
  margin: 0 auto 16px;

  @media (max-width: 480px) {
    min-height: 52px;
    margin-bottom: 10px;
  }
`;

/** Icons get a square tile; wide wordmarks show at their natural shape with no tile. */
const InvoiceLogoImg = styled.img`
  display: block;
  object-fit: contain;
  object-position: center;
  opacity: ${(p) => (p.$loaded ? 1 : 0)};

  ${(p) =>
    p.$wide
      ? `
    width: auto;
    height: auto;
    max-width: min(160px, 100%);
    max-height: 44px;

    @media (max-width: 480px) {
      max-width: min(140px, 100%);
      max-height: 38px;
    }
  `
      : `
    width: 64px;
    height: 64px;
    background: #fff;
    border-radius: 14px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

    @media (max-width: 480px) {
      width: 52px;
      height: 52px;
      border-radius: 12px;
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
    }
  `}
`;

const LogoInitialsTile = styled.div`
  width: 64px;
  height: 64px;
  background-color: #f3f4f6;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 600;
  border-radius: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin: 0 auto 16px;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

  @media (max-width: 480px) {
    width: 52px;
    height: 52px;
    font-size: 17px;
    border-radius: 12px;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
    margin-bottom: 10px;
  }
`;

const TopLeftBackButton = styled.button`
  position: absolute;
  top: 16px;
  left: 16px;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-transform: none;
  background: #ffffff;
  color: #6b7280;
  min-height: 32px;
  z-index: 10;

  @media (max-width: 480px) {
    top: 12px;
    left: 12px;
    padding: 6px 10px;
    font-size: 13px;
    min-height: 28px;
    gap: 4px;
  }

  @media (max-width: 360px) {
    top: 8px;
    left: 8px;
    padding: 5px 8px;
    font-size: 12px;
    min-height: 24px;
  }

  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    color: #374151;
  }

  @media (hover: none) {
    &:hover {
      background: #ffffff;
      border-color: #e5e7eb;
      color: #6b7280;
    }
  }

  &:active {
    background: #f3f4f6;
  }
`;

const InvoiceContainerWithBackButton = styled(InvoiceContainer)`
  position: relative;
`;

const CanceledInvoiceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: auto;
  padding: 16px;
  text-align: center;
  background: #fafafa;
  border-radius: 12px;
  margin: 16px 0;
  border: 1px solid #f0f0f0;

  @media (max-width: 480px) {
    padding: 12px;
    margin: 12px 0;
  }
`;

const CanceledMessage = styled.div`
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 16px;
  font-weight: 500;
  line-height: 1.4;
  max-width: 400px;

  @media (max-width: 480px) {
    font-size: 15px;
    margin-bottom: 12px;
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 480px) {
    gap: 8px;
    flex-direction: column;
    width: 100%;
    max-width: 200px;
  }

  @media (max-width: 360px) {
    max-width: 180px;
  }
`;

/** Must be an <a>: Text/Call use sms:/tel: hrefs, which buttons ignore. */
const ModernActionButton = styled.a`
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  gap: 6px;
  transition: all 0.2s ease;
  text-transform: none;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #374151;
  min-height: 36px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-appearance: none;
  appearance: none;

  @media (max-width: 480px) {
    padding: 6px 12px;
    font-size: 13px;
    min-height: 32px;
  }

  &:hover {
    background-color: #f9fafb;
    border-color: #d1d5db;
    color: #1f2937;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &:active {
    background-color: #f3f4f6;
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }

  @media (hover: none) {
    &:hover {
      background: #ffffff;
      border-color: #e5e7eb;
      color: #374151;
      transform: none;
      box-shadow: none;
    }
  }
`;

class Invoice extends Component {
  constructor(props) {
    super(props);
    this.paymentButtonsRef = React.createRef();
  }

  state = {
    loading: true,
    error: false,
    invoiceData: null,
    stripePromise: null,
    language: '',
    showBackButton: false,
    paymentSuccessful: false,
    logoImageError: false,
    logoLoaded: false,
    logoIsWide: false,
    /** Snapshot of PI `payment_method_types` from first load — drives which manual checkout options we show. */
    checkoutPaymentMethodSnapshot: null,
  };

  async componentDidMount() {
    await this.setLanguage();
    this.setTitle();
    await this.getInvoiceData();
    this.markAsOpened();
  }

  setLanguage = async () => {
    const language = new URLSearchParams(this.props.location.search).get(
      'language',
    );

    await this.setState({ language });
  };

  setTitle = () => {
    const { language } = this.state;
    const { title } =
      dictionary[acceptableLanguages.includes(language) ? language : 'en'];

    document.title = title;
  };

  getInvoiceData = async () => {
    try {
      const { language } = this.state;
      const { _invoice } = this.props.match.params;
      this.setState({ loading: true });

      const res = await axios.get(`/api/invoices/${_invoice}`);
      const { invoiceData, stripeAccountId } = res.data;

      // Make sure to call `loadStripe` outside of a component's render to avoid
      // recreating the `Stripe` object on every render.
      const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY, {
        locale: language || 'auto',
        stripeAccount: stripeAccountId,
      });

      const allowedTypes =
        invoiceData.paymentIntent &&
        Array.isArray(invoiceData.paymentIntent.payment_method_types)
          ? invoiceData.paymentIntent.payment_method_types
          : [];
      const enabledTypes = allowedTypes.filter((type) =>
        ENABLED_CHECKOUT_METHODS.includes(type),
      );
      const snapshot = enabledTypes.length > 0 ? enabledTypes : ['card'];

      this.setState({
        invoiceData,
        stripePromise,
        loading: false,
        logoImageError: false,
        checkoutPaymentMethodSnapshot: snapshot,
      });
    } catch (err) {
      this.setState({ error: true, loading: false });
    }
  };

  formatPhone = (phone) => {
    if (phone) {
      return phoneFormatter.format(phone, '(NNN) NNN NNNN');
    }
    return '';
  };

  handleBackButtonState = (showBack, paymentSuccessful = false) => {
    this.setState({
      showBackButton: showBack,
      paymentSuccessful: paymentSuccessful || this.state.paymentSuccessful,
    });
  };

  /** Merge latest Stripe PaymentIntent so success UI / receipt match without full page refresh. */
  handlePaymentIntentUpdated = (paymentIntent) => {
    if (!paymentIntent) return;
    this.setState((prev) => {
      if (!prev.invoiceData || !prev.invoiceData.paymentIntent) return null;
      return {
        invoiceData: {
          ...prev.invoiceData,
          paymentIntent: {
            ...prev.invoiceData.paymentIntent,
            ...paymentIntent,
          },
        },
      };
    });
  };

  handleBackButtonClick = () => {
    this.setState({ showBackButton: false });
    // Pass the callback to PaymentButtons to reset the credit card state
    if (this.paymentButtonsRef && this.paymentButtonsRef.current) {
      this.paymentButtonsRef.current.setCreditCard(false);
    }
  };

  markAsOpened = () => {
    try {
      const { _invoice } = this.props.match.params;

      axios.post('/api/mark-invoice-as-opened', {
        _invoice,
      });

      return true;
    } catch (err) {
      return err;
    }
  };

  handleLogoImageError = () => {
    this.setState({ logoImageError: true });
  };

  handleLogoImageLoad = (event) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    const logoIsWide =
      naturalHeight > 0 && naturalWidth / naturalHeight >= WIDE_LOGO_ASPECT_RATIO;
    this.setState({ logoLoaded: true, logoIsWide });
  };

  renderLogoInitials = () => {
    const { invoiceData } = this.state;
    if (!invoiceData) return null;
    const { name, username } = invoiceData;
    return (
      <LogoInitialsTile>{getBusinessInitials(name, username)}</LogoInitialsTile>
    );
  };

  renderLogo = () => {
    const { invoiceData, logoImageError, logoLoaded, logoIsWide } = this.state;
    if (!invoiceData) return null;

    const { name } = invoiceData;
    const { _invoice } = this.props.match.params;

    if (logoImageError) {
      return this.renderLogoInitials();
    }

    return (
      <InvoiceLogoSlot>
        <InvoiceLogoImg
          src={`/api/invoices/${_invoice}/logo`}
          alt={name ? `${name} logo` : ''}
          onLoad={this.handleLogoImageLoad}
          onError={this.handleLogoImageError}
          $loaded={logoLoaded}
          $wide={logoIsWide}
        />
      </InvoiceLogoSlot>
    );
  };

  renderAmount = () => {
    const { invoiceData } = this.state;
    const { paymentIntent, hasConvenienceFee } = invoiceData;
    const { amount, currency } = paymentIntent;

    if (hasConvenienceFee) {
      // calculate the original amount without the convenience fee
      const originalAmount = Math.round(amount / 1.029);

      // calculate the convenience fee
      const convenienceFee = amount - originalAmount;

      return (
        <div style={{ marginBottom: 16 }}>
          <Amount>
            {getAmountLabel({
              amount,
              currency,
            })}
          </Amount>
          <TotalAmount>
            <span>Amount:</span>
            <span>{getAmountLabel({ amount: originalAmount, currency })}</span>
          </TotalAmount>
          <ConvenienceFee>
            <span>Convenience fee:</span>
            <span>
              {getAmountLabel({
                amount: convenienceFee,
                currency,
              })}
            </span>
          </ConvenienceFee>
        </div>
      );
    }

    return <Amount>{getAmountLabel({ amount, currency })}</Amount>;
  };

  renderLoading = () => {
    const isMobile = window.innerWidth <= 480;
    const showFeeSkeleton = assumeConvenienceFeeSkeleton(
      this.props.location && this.props.location.search,
    );
    // Match renderAmount vertical space (48px/42px amount line-height 1.1 + rows + margins).
    const amountBlockMinHeight = showFeeSkeleton
      ? isMobile
        ? 120
        : 117
      : isMobile
        ? 56
        : 53;
    const feeRowMinHeight = isMobile ? 21 : 24;
    const amountBlockMarginBottom = isMobile ? 14 : 16;

    return (
      <InvoiceBody className='center'>
        <InvoiceMainRegion>
          <InvoiceContainer>
            <TopSection>
              <Skeleton
                animation='wave'
                variant='rect'
                width={isMobile ? 52 : 64}
                height={isMobile ? 52 : 64}
                style={{
                  borderRadius: isMobile ? 12 : 14,
                  display: 'block',
                  margin: isMobile ? '0 auto 10px' : '0 auto 16px',
                }}
              />
              <BusinessName style={{ textAlign: 'center' }}>
                <Skeleton
                  animation='wave'
                  height={isMobile ? 28 : 36}
                  width='80%'
                />
              </BusinessName>
              <Address style={{ textAlign: 'center' }}>
                <Skeleton
                  animation='wave'
                  height={isMobile ? 20 : 21}
                  width='60%'
                />
              </Address>
            </TopSection>
            <ModernDivider />
            <MiddleSection>
              <PaymentDetailsBox>
                <Date style={{ marginBottom: isMobile ? 4 : 6 }}>
                  <Skeleton animation='wave' height={20} width='70%' />
                </Date>
                <Description>
                  <Skeleton
                    animation='wave'
                    height={isMobile ? 24 : 22}
                    width='80%'
                  />
                </Description>
              </PaymentDetailsBox>

              <div
                style={{
                  marginBottom: amountBlockMarginBottom,
                  minHeight: amountBlockMinHeight,
                }}
              >
                <Amount
                  style={{
                    marginBottom: 10,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Skeleton
                    animation='wave'
                    height={isMobile ? 48 : 53}
                    width='60%'
                  />
                </Amount>
                {showFeeSkeleton && (
                  <React.Fragment>
                    <TotalAmount
                      style={{
                        marginBottom: isMobile ? 4 : 6,
                        minHeight: feeRowMinHeight,
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Skeleton animation='wave' height={16} width='40%' />
                      <Skeleton animation='wave' height={16} width='30%' />
                    </TotalAmount>
                    <ConvenienceFee
                      style={{
                        marginBottom: isMobile ? 14 : 16,
                        minHeight: feeRowMinHeight,
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Skeleton animation='wave' height={16} width='50%' />
                      <Skeleton animation='wave' height={16} width='25%' />
                    </ConvenienceFee>
                  </React.Fragment>
                )}
              </div>
              <ModernDivider />
              <CheckoutActionSlot>
                <Skeleton
                  animation='wave'
                  variant='rect'
                  height={48}
                  width='100%'
                  style={{ borderRadius: 4, marginBottom: 8 }}
                />
                <Skeleton
                  animation='wave'
                  variant='rect'
                  height={40}
                  width='100%'
                  style={{ borderRadius: 4 }}
                />
              </CheckoutActionSlot>
            </MiddleSection>
          </InvoiceContainer>
        </InvoiceMainRegion>
        <Branding>
          <Skeleton
            animation='wave'
            height={14}
            width='120px'
            style={{ visibility: 'hidden' }}
          />
        </Branding>
      </InvoiceBody>
    );
  };

  renderError = () => {
    const { language } = this.state;
    const { errorTitle, errorMessage } =
      dictionary[acceptableLanguages.includes(language) ? language : 'en'];

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          padding: '24px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '64px',
            height: '64px',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
            border: '1px solid #fecaca',
            marginBottom: '24px',
          }}
        >
          <svg width='32' height='32' viewBox='0 0 24 24' fill='none'>
            <path
              d='M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z'
              stroke='#dc2626'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>

        <h2
          style={{
            color: '#1f2937',
            margin: '0 0 12px 0',
            fontSize: '24px',
            fontWeight: '600',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          {errorTitle}
        </h2>

        <p
          style={{
            color: '#6b7280',
            margin: '0',
            fontSize: '16px',
            lineHeight: '1.5',
            maxWidth: '400px',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          {errorMessage}
        </p>
      </div>
    );
  };

  renderCheckoutForm = () => {
    const { invoiceData, language } = this.state;
    const { _invoice } = this.props.match.params;
    const { paymentSuccessTitle, paymentSuccessBody, voidMessage, text, call } =
      dictionary[acceptableLanguages.includes(language) ? language : 'en'];

    if (!invoiceData) {
      return <div />;
    }

    const {
      brandColor,
      paymentIntent,
      country,
      name: businessName,
      customerName,
      customerEmail,
      twilioNumber,
      hasConvenienceFee,
    } = invoiceData;

    const { amount } = paymentIntent;

    // calculate the original amount without the convenience fee
    const originalAmount = Math.round(amount / 1.029);

    // calculate the convenience fee
    const convenienceFee = amount - originalAmount;

    const { client_secret: clientSecret, currency, status } = paymentIntent;

    if (paymentIntent && status === 'succeeded') {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              marginBottom: '12px',
              padding: '12px 14px',
              background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
              borderRadius: '12px',
              border: '1px solid #bbf7d0',
              gap: '10px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                background: CHEKKIT_GREEN,
                flexShrink: 0,
              }}
            >
              <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                <path
                  d='M9 12L11 14L15 10'
                  stroke='white'
                  strokeWidth='2.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h4
                style={{
                  color: '#166534',
                  margin: '0 0 2px 0',
                  fontSize: '15px',
                  fontWeight: '600',
                  lineHeight: 1.3,
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                }}
              >
                {paymentSuccessTitle}
              </h4>
              <p
                style={{
                  color: '#166534',
                  margin: '0',
                  fontSize: '13px',
                  fontWeight: '400',
                  lineHeight: 1.4,
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                }}
              >
                {paymentSuccessBody}
              </p>
            </div>
          </div>
          <div
            style={{
              minHeight: 56,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
            }}
          >
            {hasConvenienceFee ? (
              <Receipt
                invoiceData={invoiceData}
                convenienceFee={convenienceFee}
                originalAmount={originalAmount}
                hasConvenienceFee={hasConvenienceFee}
                language={language}
                _invoice={_invoice}
                currency={currency}
              />
            ) : (
              <Receipt
                invoiceData={invoiceData}
                hasConvenienceFee={hasConvenienceFee}
                language={language}
                _invoice={_invoice}
                currency={currency}
              />
            )}
          </div>
        </div>
      );
    }

    if (status === 'canceled') {
      const contactNumber =
        typeof twilioNumber === 'string' ? twilioNumber.trim() : '';

      return (
        <CanceledInvoiceContainer>
          <CanceledMessage>{voidMessage}</CanceledMessage>
          {contactNumber ? (
            <ActionButtonsContainer>
              <ModernActionButton href={`sms:${contactNumber}`}>
                <MessageIcon />
                <span>{text}</span>
              </ModernActionButton>
              <ModernActionButton href={`tel:${contactNumber}`}>
                <CallIcon />
                <span>{call}</span>
              </ModernActionButton>
            </ActionButtonsContainer>
          ) : null}
        </CanceledInvoiceContainer>
      );
    }

    return (
      <PaymentButtons
        ref={this.paymentButtonsRef}
        amount={amount}
        businessName={businessName}
        country={country}
        currency={currency}
        brandColor={brandColor}
        clientSecret={clientSecret}
        language={language}
        _invoice={_invoice}
        invoiceData={invoiceData}
        customerName={customerName}
        customerEmail={customerEmail}
        checkoutPaymentMethodTypes={
          this.state.checkoutPaymentMethodSnapshot || ['card']
        }
        onBackButtonStateChange={this.handleBackButtonState}
        onPaymentIntentUpdated={this.handlePaymentIntentUpdated}
      />
    );
  };

  renderInvoice = () => {
    const { language, invoiceData } = this.state;

    const { billed, forText, requested } =
      dictionary[acceptableLanguages.includes(language) ? language : 'en'];

    const {
      name,
      address,
      paymentIntent,
      customerName,
      customerPhone,
      customerEmail,
    } = invoiceData;

    // the name to show for the customer (can be name phone or email )
    let customerNameToShow;

    if (customerName && customerName !== '') {
      customerNameToShow = customerName;
    } else if (customerPhone) {
      customerNameToShow = this.formatPhone(customerPhone);
    } else if (customerEmail) {
      customerNameToShow = customerEmail;
    }

    const { created, description, status } = paymentIntent;

    const { showBackButton, paymentSuccessful } = this.state;
    const { back } =
      dictionary[
        acceptableLanguages.includes(this.state.language)
          ? this.state.language
          : 'en'
      ];

    // Hide back button when payment is successful (either server-side or client-side)
    const shouldShowBackButton =
      showBackButton && status !== 'succeeded' && !paymentSuccessful;

    return (
      <InvoiceContainerWithBackButton>
        {shouldShowBackButton && (
          <TopLeftBackButton type='button' onClick={this.handleBackButtonClick}>
            <ArrowBackIcon /> {back}
          </TopLeftBackButton>
        )}
        <TopSection>
          {this.renderLogo()}
          <BusinessName>{name}</BusinessName>
          <Address>{address}</Address>
        </TopSection>
        <ModernDivider />
        <MiddleSection>
          <PaymentDetailsBox>
            <Date>{`${requested} ${getDateLabel(created)}`}</Date>
            <Description>{`${billed} ${customerNameToShow} ${forText} ${description}`}</Description>
          </PaymentDetailsBox>

          {this.renderAmount()}
          <ModernDivider />
          <CheckoutActionSlot>{this.renderCheckoutForm()}</CheckoutActionSlot>
        </MiddleSection>
      </InvoiceContainerWithBackButton>
    );
  };

  render() {
    const { error, stripePromise, loading, language, invoiceData } = this.state;
    const { branding } =
      dictionary[acceptableLanguages.includes(language) ? language : 'en'];

    if (error) {
      return this.renderError();
    }

    if (loading) {
      return this.renderLoading();
    }

    const paymentIntent = invoiceData && invoiceData.paymentIntent;
    // Succeeded/canceled intents cannot start a new Elements session; keeping
    // <Elements clientSecret> mounted causes GET /v1/elements/sessions → 400.
    const skipStripeElementsContext =
      paymentIntent &&
      (paymentIntent.status === 'succeeded' ||
        paymentIntent.status === 'canceled');

    const invoiceLayout = (
      <InvoiceBody className='center'>
        <InvoiceMainRegion>{this.renderInvoice()}</InvoiceMainRegion>
        <Branding
          href='https://www.chekkit.io?utm_source=payment_page&utm_medium=invoice&utm_campaign=powered_by'
          target='_blank'
        >
          {branding} Chekkit
        </Branding>
      </InvoiceBody>
    );

    if (skipStripeElementsContext) {
      return invoiceLayout;
    }

    // Deferred-intent mode: Stripe can't narrow an existing PaymentIntent's methods
    // (chekkit-rep creates them with automatic_payment_methods), so Elements lists the
    // enabled ones itself and confirmPayment() still pays the existing intent by client secret.
    const elementsOptions = paymentIntent
      ? {
          mode: 'payment',
          amount: paymentIntent.amount,
          currency: paymentIntent.currency,
          paymentMethodTypes: this.state.checkoutPaymentMethodSnapshot || ['card'],
          // Required for stripe.createPaymentMethod({ elements }) in confirmDeferredPayment.
          paymentMethodCreation: 'manual',
          locale: language || 'auto',
          appearance: getStripeInvoiceAppearance(),
        }
      : undefined;

    // Remount Stripe context when the PaymentIntent (and thus client secret) changes
    // so the Payment Element never stays bound to stale intent data.
    const elementsKey = paymentIntent
      ? paymentIntent.client_secret
      : 'no-client-secret';

    return (
      <Elements key={elementsKey} stripe={stripePromise} options={elementsOptions}>
        {invoiceLayout}
      </Elements>
    );
  }
}

export default Invoice;
